import { useEffect, useState } from "react";
import type { DealSummary } from "@edge/shared";

export interface SaveNoteInput {
  deal_id: string;
  content: string;
}

export interface SaveNoteDialogProps {
  initial: {
    content: string;
    /** Pre-selected deal id, e.g. from detectDealFromAlert. */
    deal_id?: string;
  };
  deals: DealSummary[];
  /** Called when the user confirms. May throw — error shown inline. */
  onSave: (input: SaveNoteInput) => Promise<void>;
  onCancel: () => void;
}

/**
 * Append a note to a Zoho Deal. Note must be linked to a deal (Zoho requires
 * Parent_Id + se_module). When no deal is auto-detected, the SAVE button is
 * disabled and re-labeled "PICK A DEAL" so it's obvious why it's gray.
 */
export function SaveNoteDialog({
  initial,
  deals,
  onSave,
  onCancel,
}: SaveNoteDialogProps) {
  const [content, setContent] = useState(initial.content);
  const [dealId, setDealId] = useState(initial.deal_id ?? "");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const canSave = Boolean(dealId) && content.trim().length > 0 && !running;
  const selectedDealName =
    deals.find((d) => d.id === dealId)?.name ?? null;

  const handleSave = async () => {
    if (!dealId) return;
    const trimmed = content.trim();
    if (!trimmed) {
      setError("content_required");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      await onSave({ deal_id: dealId, content: trimmed });
    } catch (err) {
      setError(err instanceof Error ? err.message : "write_failed");
      setRunning(false);
    }
    // Parent unmounts on success.
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card save-note-card">
        <div className="write-confirm-title">Save note to Zoho</div>
        <div className="crm-create-body">
          <label className="crm-create-field">
            <span className="crm-create-label">Note</span>
            <textarea
              className="crm-create-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={6}
              autoFocus={Boolean(initial.deal_id)}
            />
          </label>
          <label className="crm-create-field">
            <span className="crm-create-label">
              Linked deal {dealId ? "" : "· required"}
            </span>
            <select
              className="crm-create-input"
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              autoFocus={!initial.deal_id}
            >
              <option value="">(pick a deal)</option>
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleSave}
            disabled={!canSave}
            title={
              !dealId
                ? "Pick a deal first"
                : selectedDealName
                  ? `Save to ${selectedDealName}`
                  : undefined
            }
          >
            {running ? "WRITING…" : !dealId ? "PICK A DEAL" : "SAVE NOTE"}
          </button>
        </div>
      </div>
    </div>
  );
}
